import axios, { AxiosResponse, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { storage } from '../utils/storage';

const MAX_429_RETRIES = 2;
const RETRY_DELAYS_MS = [2000, 5000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface ConfigWithRetry extends InternalAxiosRequestConfig {
  _retry429Count?: number;
}

// Bestimme die baseURL basierend auf der Umgebung
const getBaseURL = (): string => {
  // In der Entwicklung: Proxy wird verwendet (keine baseURL nötig)
  if (import.meta.env.DEV) {
    return '';
  }
  
  // In Production: Verwende die aktuelle Domain
  // Wenn die App auf www.kingofants.com läuft, werden API-Anfragen automatisch
  // an die gleiche Domain gesendet (Reverse-Proxy sollte /api weiterleiten)
  return window.location.origin;
};

// Erstelle eine konfigurierte axios-Instance
const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000, // 30 Sekunden Timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor für Debugging und userId-Header
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Prüfe ob es ein FormData ist (für File-Uploads)
    const isFormData = config.data instanceof FormData;
    
    // Wenn FormData, entferne Content-Type Header (axios setzt ihn automatisch mit boundary)
    if (isFormData) {
      delete config.headers['Content-Type'];
    }
    
    // Prüfe ob es ein Admin-Endpoint ist
    const isAdminEndpoint = config.url?.includes('/api/admin/');
    
    // Füge JWT-Token zu allen Requests hinzu
    const token = storage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Füge userId zu allen Requests hinzu
    const storedUser = storage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user?.id) {
          // Für GET-Requests: userId als Query-Parameter
          if (config.method === 'get' || config.method === 'GET') {
            config.params = config.params || {};
            
            // Bei Admin-Endpoints: userId IMMER hinzufügen für Authentifizierung
            // WICHTIG: userId wird für Auth benötigt, filterUserId wird für Filterung verwendet
            if (isAdminEndpoint) {
              // userId wird IMMER für Auth benötigt - füge es hinzu wenn nicht vorhanden
              // filterUserId wird separat vom Frontend gesetzt für Filterung
              if (!config.params.userId) {
                config.params.userId = user.id;
              }
            } else {
              // Bei normalen Endpoints: userId immer hinzufügen wenn nicht vorhanden
              if (!config.params.userId) {
                config.params.userId = user.id;
              }
            }
          }
          // Für POST/PUT/PATCH/DELETE: userId im Body oder Header
          else {
            // Wenn FormData: userId als Query-Parameter oder Header hinzufügen
            if (isFormData) {
              // Für FormData: userId als Query-Parameter hinzufügen
              config.params = config.params || {};
              if (!config.params.userId) {
                config.params.userId = user.id;
              }
              // Alternativ als Header
              if (!config.headers['x-user-id']) {
                config.headers['x-user-id'] = String(user.id);
              }
            } else {
              // Wenn Body bereits ein Objekt ist, füge userId hinzu
              if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
                if (!config.data.userId) {
                  config.data = { ...config.data, userId: user.id };
                }
              }
              // Alternativ als Header
              if (!config.headers['x-user-id']) {
                config.headers['x-user-id'] = String(user.id);
              }
            }
          }
        }
      } catch {
        // Ignoriere Fehler beim Parsen von user
      }
    }

    // Sprache bei allen Requests mitschicken
    const lang = storage.getItem('language') || 'de';
    if (config.method === 'get' || config.method === 'GET') {
      config.params = { ...config.params, lang };
    }
    config.headers['X-Language'] = lang;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Fehlerbehandlung + 429-Retry mit Backoff
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as ConfigWithRetry | undefined;

    // 429 Too Many Requests: begrenzt erneut versuchen mit Wartezeit
    if (status === 429 && config) {
      const count = typeof config._retry429Count === 'number' ? config._retry429Count : 0;
      if (count < MAX_429_RETRIES) {
        const delay = RETRY_DELAYS_MS[count] ?? 5000;
        config._retry429Count = count + 1;
        if (import.meta.env.DEV) {
          console.warn(`[Axios] 429 – erneuter Versuch in ${delay / 1000}s (${count + 1}/${MAX_429_RETRIES})`, config.url);
        }
        await sleep(delay);
        return axiosInstance.request(config);
      }
    }

    if (import.meta.env.DEV) {
      console.error('[Axios Error]', status, error.response?.data || error.message);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
